import { computed, ref, toValue, watch } from 'vue';
import { administrationTypeOf, carrierOf, doseQuantityOf, isDoseEditable, isHeld, isPrn, segmentChain, segmentOn, sigTextOf, todayIn } from '@/common/medicationSchedule';
import { DAY_NAMES, enrolledSiteOf, intervalLabel, segmentEndDay, segmentStartDay, unitName } from '@/common/medicationWindow';
import { Dosage, DosageDoseAndRate } from '@/fhirworks';
import { formatClockTime, formatDate } from '@/filters/dateTimeFilters';
import { addDays, format, parseISO } from 'date-fns';

// UCUM has no code for most dose forms, so these are its curly-brace annotations. A unit
// typed by hand is carried as a display unit alone rather than as an invented code.
export const DOSE_UNIT_CODES = {
    Tablet: '{tbl}',
    Capsule: '{capsule}',
    mL: 'mL',
    mg: 'mg',
    g: 'g',
    mcg: 'ug',
    Patch: '{patch}',
    Spray: '{spray}',
    Drop: '{drp}',
    Unit: '{unit}',
    Suppository: '{suppository}',
    Puff: '{puff}',
};

// Unknown is a statement, not a missing answer: a medication recorded at intake before anyone has
// confirmed how it is taken. It writes no order and is never locked, so the order is written later.
export const administrationTypeOptions = [
    { value: 'unknown', title: 'Unknown', chip: 'Unknown' },
    { value: 'scheduled', title: 'Scheduled', chip: 'Scheduled' },
    { value: 'taper', title: 'Taper', chip: 'Taper' },
    { value: 'prn', title: 'PRN (as needed)', chip: 'PRN' },
];

const segmentStartOf = (segment) => segment?.timing?.repeat?.boundsPeriod?.start || '';

// The carrier is the medication-level dosageInstruction entry — the one with no segment start.
// Kept at index 0 because useScriptSureClinicalSync reads the dose from dosageInstruction[0].text
// and the frequency from dosageInstruction[0].timing.code.text.
export const ensureCarrier = (med) => {
    const existing = carrierOf(med);
    if (existing) return existing;
    const carrier = new Dosage({});
    med.dosageInstruction = [carrier, ...(med.dosageInstruction || [])];
    return carrier;
};

// `timing` is a Timing instance whose declared fields are not own enumerable properties, so
// spreading it drops them.
export const mergeCarrierTiming = (carrier, patch) => {
    const existing = carrier?.timing;
    const next = {};

    const code = 'code' in patch ? patch.code : existing?.code?.text ? { text: existing.code.text } : undefined;
    if (code?.text) next.code = code;

    const repeat =
        'repeat' in patch
            ? patch.repeat
            : existing?.repeat?.frequency || existing?.repeat?.period
              ? { frequency: existing.repeat.frequency, period: existing.repeat.period, periodUnit: existing.repeat.periodUnit }
              : undefined;
    if (repeat && Object.values(repeat).some((value) => value !== undefined)) next.repeat = repeat;

    if (existing?.administrationWindow !== undefined) next.administrationWindow = existing.administrationWindow;

    return next;
};

// Sequence has to follow start order: the resolver walks the chain by sequence. Every segment but
// the last is closed by its successor's start, so the stored end has to say so.
export const rechainSegments = (med) => {
    const entries = med.dosageInstruction || [];
    const carriers = entries.filter((entry) => !segmentStartOf(entry));
    const segments = entries.filter((entry) => segmentStartOf(entry)).sort((a, b) => String(segmentStartOf(a)).localeCompare(String(segmentStartOf(b))));

    segments.forEach((segment, index) => {
        segment.sequence = index + 1;

        const nextStart = segmentStartDay(segments[index + 1]);
        const bounds = segment.timing?.repeat?.boundsPeriod;
        if (!nextStart || !bounds || nextStart <= segmentStartDay(segment)) return;
        const closesOn = format(addDays(parseISO(nextStart), -1), 'yyyy-MM-dd');
        // Narrowing only. Stretching a segment that stopped earlier resurrects every dose in the gap.
        if (bounds.end && String(bounds.end).slice(0, 10) <= closesOn) return;
        bounds.end = closesOn;
    });

    med.dosageInstruction = [...carriers, ...segments];
};

// The order setters below are the add dialog's, kept pure so a medication built outside the dialog
// (a preset applied to a patient) is written exactly as one entered by hand.

export const applyOrderTiming = (med, patch) => {
    const carrier = ensureCarrier(med);
    const current = carrier.timing?.repeat || {};
    const next = { frequency: current.frequency ?? null, period: current.period ?? null, periodUnit: current.periodUnit || 'd', ...patch };

    const repeat = {};
    const frequency = Number(next.frequency);
    if (Number.isFinite(frequency) && frequency > 0) repeat.frequency = frequency;
    const period = Number(next.period);
    // period and periodUnit are written together or not at all — Aidbox rejects a partial repeat.
    if (Number.isFinite(period) && period > 0 && next.periodUnit) {
        repeat.period = period;
        repeat.periodUnit = next.periodUnit;
    }

    carrier.timing = mergeCarrierTiming(carrier, { repeat: Object.keys(repeat).length ? repeat : undefined });
};

export const applyOrderPrn = (med, patch) => {
    const carrier = ensureCarrier(med);
    if ('maxPerDay' in patch) {
        const value = Number(patch.maxPerDay);
        carrier.maxPerDay = Number.isFinite(value) && value > 0 ? value : undefined;
    }
    if ('minimumIntervalValue' in patch || 'minimumIntervalUnit' in patch) {
        const value = Number(patch.minimumIntervalValue ?? carrier.minimumInterval?.value);
        const code = patch.minimumIntervalUnit ?? carrier.minimumInterval?.code ?? 'h';
        const unit = code === 'h' ? 'hours' : 'minutes';
        carrier.minimumInterval = Number.isFinite(value) && value > 0 ? { value, unit, system: 'http://unitsofmeasure.org', code } : undefined;
    }
};

/**
 * The sig composed from the order as it stands.
 *
 * @param {object|null} med
 * @param {{administrationType: string, currentSegment?: object|null}} options
 * @returns {string}
 */
export const composeSig = (med, { administrationType, currentSegment = null }) => {
    const carrier = carrierOf(med);
    const dose = doseQuantityOf(carrier) ?? null;
    const reportedAsNeeded = administrationType !== 'prn' && carrier?.asNeeded?.boolean === true;
    if (!dose?.value) return reportedAsNeeded ? 'as needed' : '';

    const parts = [`${dose.value} ${dose.unit || ''}`.trim()];
    if (carrier?.route?.text) parts.push(`by ${String(carrier.route.text).toLowerCase()} route`);

    if (administrationType === 'prn') {
        const max = carrier?.maxPerDay;
        const interval = carrier?.minimumInterval;
        if (max) parts.push(`up to ${max} times daily`);
        if (interval?.value) parts.push(`at least ${interval.value} ${unitName(interval.code, interval.value)} apart`);
        parts.push('as needed');
    } else if (administrationType !== 'unknown') {
        const timing = carrier?.timing?.repeat ?? null;
        if (timing?.frequency) {
            parts.push(`${timing.frequency} times ${intervalLabel(timing)}`);
            // A schedule that starts next week still has to describe itself today.
            const scheduled = currentSegment || segmentChain(med)[0];
            const days = (scheduled?.timing?.repeat?.dayOfWeek || []).map((day) => DAY_NAMES[day] || day);
            if (days.length) parts.push(`on ${days.join(', ')}`);
        }
        if (reportedAsNeeded) parts.push('as needed');
    }

    const sig = parts.filter(Boolean).join(' ');
    return administrationType === 'taper' ? `${sig}, tapering — see schedule` : sig;
};

export const applyOrderDose = (med, patch) => {
    const current = doseQuantityOf(carrierOf(med)) ?? null;
    const next = { value: current?.value ?? null, unit: current?.unit || '', ...patch };
    const amount = next.value === '' || next.value === null || next.value === undefined ? NaN : Number(next.value);
    const unit = String(next.unit || '').trim();
    const carrier = ensureCarrier(med);

    if (!Number.isFinite(amount) && !unit) {
        carrier.doseAndRate = [];
        return;
    }

    const quantity = {};
    if (Number.isFinite(amount)) quantity.value = amount;
    if (unit) {
        quantity.unit = unit;
        const code = DOSE_UNIT_CODES[unit];
        if (code) {
            quantity.system = 'http://unitsofmeasure.org';
            quantity.code = code;
        }
    }
    carrier.doseAndRate = [new DosageDoseAndRate({ dose: { Quantity: quantity } })];
};

export const applyOrderRoute = (med, value) => {
    const route = String(value || '').trim();
    ensureCarrier(med).route = route ? { text: route } : undefined;
};

export const applyAdministrationInstructions = (med, value) => {
    ensureCarrier(med).patientInstruction = value || undefined;
};

/**
 * Editing state and mutations for the one medication order open in the edit dialog.
 *
 * @param {object} options
 * @param {import('vue').Ref<object|null>} options.editingMedication
 * @param {import('vue').Ref<object|null>} options.originalMedication
 * @param {import('vue').Ref<boolean>} options.isNewMedication
 * @param {import('vue').MaybeRefOrGetter<boolean>} options.editingMedIsPrescribed
 * @param {import('vue').MaybeRefOrGetter<boolean>} options.readOnly
 * @param {import('vue').MaybeRefOrGetter<boolean>} options.viewOnlyMedications
 * @param {import('vue').MaybeRefOrGetter<boolean>} options.isMedicationSelected
 * @param {import('vue').MaybeRefOrGetter<string>} options.scheduleTimeZone
 * @param {import('vue').Ref<Array<{id: string, label: string}>>} options.administrationSites
 * @param {import('vue').MaybeRefOrGetter<boolean>} options.editDialog
 * @param {(med: object) => string|null} options.endDayOf - the day an order stops, in the service zone
 * @param {(options: object) => Promise<boolean>|undefined} options.confirm - asks before a destructive change
 * @param {import('vue').MaybeRefOrGetter<boolean>} [options.isPortal] - a patient reporting what they take, not an order being written
 */
export function useMedicationOrderEditor({
    editingMedication,
    originalMedication,
    isNewMedication,
    editingMedIsPrescribed,
    readOnly,
    viewOnlyMedications,
    isMedicationSelected,
    scheduleTimeZone,
    administrationSites,
    editDialog,
    endDayOf,
    confirm,
    isPortal = false,
}) {
    const todayInServiceZone = () => todayIn(toValue(scheduleTimeZone));

    const segmentDialog = ref(false);
    const editingSegment = ref(null);
    const initialSegmentType = ref(null);
    const segmentTemplate = ref(null);
    const taperDialog = ref(false);

    const editingSegments = computed(() => (editingMedication.value ? segmentChain(editingMedication.value) : []));

    const canEditAdministrationType = computed(() => !toValue(viewOnlyMedications) && !toValue(readOnly));

    // Deliberately not gated on editingMedIsPrescribed (D9): a ScriptSure order is exactly the case
    // that needs a schedule.
    const canEditSchedule = computed(() => canEditAdministrationType.value && toValue(isMedicationSelected));

    const canOpenSegment = computed(() => canEditSchedule.value || toValue(viewOnlyMedications));

    // Drug, strength, form and route are what doses and administration records are keyed to, so a
    // correction on a saved order is a discontinue-and-re-add (D15). A medication saved as Unknown has
    // no order yet, and nothing has been given of it, so what it is stays correctable.
    const canEditOrderIdentity = computed(
        () => (isNewMedication.value || (savedScheduleType.value === 'unknown' && !isDoseLocked.value)) && !toValue(editingMedIsPrescribed) && !toValue(viewOnlyMedications) && !toValue(readOnly),
    );

    const canEditRoute = computed(() => (canEditOrderIdentity.value || (canEditSchedule.value && !isDoseLocked.value)) && toValue(isMedicationSelected));

    // A taper step's dose differs from the medication's by design, so every row states its own.
    const segmentDoseText = (segment, med) => {
        const dose = doseQuantityOf(segment) ?? doseQuantityOf(carrierOf(med));
        if (dose?.value === undefined || dose?.value === null) return null;
        return `${dose.value} ${dose.unit || ''}`.trim();
    };

    const describeSegment = (segment, med = editingMedication.value) => {
        if (isHeld(segment)) return `Hold — ${segment.hold.reason?.text || segment.hold.reason?.coding?.[0]?.display || 'no reason recorded'}`;

        const dose = segmentDoseText(segment, med);

        if (isPrn(segment)) {
            const max = segment.maxPerDay ? `max ${segment.maxPerDay}/day` : null;
            const interval = segment.minimumInterval?.value ? `min ${segment.minimumInterval.value} ${segment.minimumInterval.unit || ''}`.trim() : null;
            return [dose, 'PRN', max, interval].filter(Boolean).join(' · ');
        }

        const repeat = segment?.timing?.repeat;
        const times = (repeat?.timeOfDay || []).map((time) => formatClockTime(time)).join(', ');
        const days = (repeat?.dayOfWeek || []).map((day) => DAY_NAMES[day] || day).join(', ');
        // A taper step states its own interval, so 'every week' reading like 'every day' would hide the taper.
        const isDaily = (repeat?.period ?? 1) === 1 && (repeat?.periodUnit || 'd') === 'd';
        const every = isDaily ? null : intervalLabel({ period: repeat.period, periodUnit: repeat.periodUnit });
        return [dose || 'Scheduled', times, days, every].filter(Boolean).join(' · ');
    };

    /**
     * Close the schedule on the day the medication stopped, so the chain agrees with the resolver.
     * Earlier segments are untouched, which keeps a prior day's report the same. A segment starting
     * after the stop is removed: the resolver reads a segment with a successor as running until that
     * successor starts, whatever end it states.
     *
     * @param {object} med
     * @param {string} endDate - 'YYYY-MM-DD'
     */
    const endSegmentChain = (med, endDate) => {
        if (!endDate) return;

        const unstarted = segmentChain(med).filter((segment) => segmentStartDay(segment) > endDate);
        if (unstarted.length) med.dosageInstruction = (med.dosageInstruction || []).filter((entry) => !unstarted.includes(entry));

        const running = segmentOn(med, endDate, toValue(scheduleTimeZone));
        if (!running) return;
        // Only ever shortens: extending a segment that already ended would expect doses nobody ordered.
        if (segmentEndDay(running) && segmentEndDay(running) <= endDate) return;

        if (!running.timing) running.timing = {};
        if (!running.timing.repeat) running.timing.repeat = {};
        if (!running.timing.repeat.boundsPeriod) running.timing.repeat.boundsPeriod = {};
        running.timing.repeat.boundsPeriod.end = endDate;
    };

    // endSegmentChain leaves the running segment last, ending on the stop day. The day is taken in the
    // service zone: a stop today is stored as a UTC instant, which is already tomorrow there by evening.
    const reopenSegmentChain = (med) => {
        const stoppedOn = endDayOf(med);
        const chain = segmentChain(med);
        const last = chain[chain.length - 1];
        if (!stoppedOn || !last || segmentEndDay(last) !== stoppedOn) return;
        delete last.timing.repeat.boundsPeriod.end;
    };

    /**
     * @param {object} med - a copy, persisted by the caller
     * @param {{endDate: string, endsAt: string, status: string}} stop
     */
    const discontinueOrder = (med, { endDate, endsAt, status }) => {
        if (!med.effective) med.effective = {};
        med.effective.end = endsAt;
        med.status = status;
        endSegmentChain(med, endDate);
    };

    const restoreOrder = (med, wasDiscontinued) => {
        if (wasDiscontinued) reopenSegmentChain(med);
        if (med.effective) med.effective = { start: med.effective.start };
        med.status = 'active';
        med.statusReason = undefined;
    };

    const editingCarrier = computed(() => carrierOf(editingMedication.value));

    const editingDose = computed(() => doseQuantityOf(editingCarrier.value) ?? null);

    const editingOrderTiming = computed(() => editingCarrier.value?.timing?.repeat ?? null);

    const orderFrequency = computed(() => {
        const value = Number(editingOrderTiming.value?.frequency);
        return Number.isFinite(value) && value > 0 ? value : null;
    });

    const orderPeriod = computed(() => {
        const value = Number(editingOrderTiming.value?.period);
        return Number.isFinite(value) && value > 0 ? value : 1;
    });

    const orderPeriodUnit = computed(() => editingOrderTiming.value?.periodUnit || 'd');

    // Every order field locks on save, so requiring them afterwards would trap a legacy record. A
    // patient in the portal is reporting, not ordering: a name alone is enough for the practice to
    // reconcile, and a guessed dose is worse than a blank. A medication saved as Unknown is the one
    // saved record whose order is still to be written - asked only while its fields can answer.
    const isOrderBeingWritten = () => (isNewMedication.value || (savedScheduleType.value === 'unknown' && canEditDose.value)) && !toValue(isPortal);
    // Unknown asks for none of the order: a made-up frequency would become what the eMAR gives from.
    const requiresDose = () => isOrderBeingWritten() && administrationType.value !== 'unknown';
    const requiresRecurrence = () => isOrderBeingWritten() && (administrationType.value === 'scheduled' || administrationType.value === 'taper');
    const requiresPrnLimits = () => isOrderBeingWritten() && administrationType.value === 'prn';

    // buildSegment throws without both limits. Only asked of a form that can still answer it: a saved
    // order switched to PRN shows limits it cannot type into.
    const prnOrderIncomplete = computed(
        () => canEditDose.value && administrationType.value === 'prn' && !(Number(editingCarrier.value?.maxPerDay) > 0 && Number(editingCarrier.value?.minimumInterval?.value) > 0),
    );

    // D14, for the one schedule date entered on this form. Once the chain exists the start is locked,
    // and judging it would lock Save on every PRN order the day after it began.
    const writesPrnWindow = computed(() => administrationType.value === 'prn' && !editingSegments.value.length);
    const prnStartsInPast = computed(() => writesPrnWindow.value && Boolean(prnStart.value) && prnStart.value < todayInServiceZone());

    const effectiveStart = computed(() => String(editingMedication.value?.effective?.start || '').slice(0, 10) || null);
    const effectiveEnd = computed(() => String(editingMedication.value?.effective?.end || '').slice(0, 10) || null);

    const effectiveEndChanged = computed(() => effectiveEnd.value !== (String(originalMedication.value?.effective?.end || '').slice(0, 10) || null));

    // Only a change is refused: an e-prescription can arrive with a course that has already run.
    const effectiveEndInPast = computed(() => effectiveEndChanged.value && Boolean(effectiveEnd.value) && effectiveEnd.value < todayInServiceZone());

    const lastWindowDay = computed(() =>
        editingSegments.value.reduce((latest, segment) => {
            const day = segmentEndDay(segment) || segmentStartDay(segment);
            return day && day > latest ? day : latest;
        }, ''),
    );
    const effectiveEndBeforeWindow = computed(() => effectiveEndChanged.value && Boolean(effectiveEnd.value && lastWindowDay.value) && effectiveEnd.value < lastWindowDay.value);

    const effectiveEndBeforeStart = computed(() => Boolean(effectiveStart.value && effectiveEnd.value) && effectiveEnd.value < effectiveStart.value);

    const effectiveEndError = computed(() => {
        if (effectiveEndBeforeStart.value) return 'Ends on or after the start.';
        if (effectiveEndInPast.value) return 'The end date cannot move into the past.';
        if (effectiveEndBeforeWindow.value) return `A schedule window runs to ${formatDate(lastWindowDay.value)}. Shorten it first.`;
        return '';
    });

    // Only while its pickers are open: a refusal nobody can act on only closes Save.
    const prnWindowEditable = computed(() => writesPrnWindow.value && canEditSchedule.value && !isTypeLocked.value);

    const prnStartError = computed(() => (prnWindowEditable.value && effectiveEnd.value && prnStart.value > effectiveEnd.value ? `The order ends ${formatDate(effectiveEnd.value)}.` : ''));

    const prnEndError = computed(() => {
        if (!prnWindowEditable.value || !prnEnd.value) return '';
        if (prnStart.value && prnEnd.value < prnStart.value) return 'The end date cannot be before the start date.';
        if (effectiveEnd.value && prnEnd.value > effectiveEnd.value) return `The order ends ${formatDate(effectiveEnd.value)}. End on or before then.`;
        return '';
    });

    const setOrderTiming = (patch) => {
        const med = editingMedication.value;
        if (!med) return;
        applyOrderTiming(med, patch);
    };

    const prescribedSig = computed(() => sigTextOf(editingMedication.value));

    // A prescribed medication's carrier text IS its sig, already shown as the Sig.
    const editingDoseFreeText = computed(() => (toValue(editingMedIsPrescribed) ? '' : editingCarrier.value?.text || ''));

    const currentSegment = computed(() => segmentOn(editingMedication.value, todayInServiceZone(), toValue(scheduleTimeZone)));

    const isSegmentSaved = (segment) => {
        const start = segmentStartOf(segment);
        if (!start) return false;
        return segmentChain(originalMedication.value).some((entry) => segmentStartOf(entry) === start);
    };

    // Starts true so the dose stays locked while the count is in flight: guessing "never given" and
    // being wrong unlocks a field administration records are keyed to.
    const editingHasAdministrations = ref(true);

    const isDoseLocked = computed(() => {
        if (!editingMedication.value || isNewMedication.value) return false;
        return !isDoseEditable(editingMedication.value, { hasAdministrations: editingHasAdministrations.value });
    });

    const canEditDose = computed(() => canEditSchedule.value && !isDoseLocked.value);

    const savedScheduleType = computed(() => administrationTypeOf(originalMedication.value) || 'none');

    const scheduleSectionCy = computed(() => (isNewMedication.value ? 'initialSegmentSection' : 'segmentChainSection'));

    // Unknown stays open: it is saved so the medication is on the list, and settled when a type is.
    const isTypeLocked = computed(() => !isNewMedication.value && savedScheduleType.value !== 'none' && savedScheduleType.value !== 'unknown');

    const administrationType = ref('scheduled');

    const administrationTypeLabel = computed(() => administrationTypeOptions.find((option) => option.value === administrationType.value)?.chip || '');

    const segmentSiteLabel = (segment) => administrationSites.value.find((site) => site.id === segment?.administeringLocation?.id)?.label || '';

    const setAdministrationSite = (id) => {
        // Aidbox rejects a null.
        editingMedication.value.administeringLocation = id ? { id, resourceType: 'Location' } : undefined;
    };

    // Only where there is exactly one enrollment: with two, picking one is the arbitrary choice the
    // field exists to remove. Only on a PRN order, because a scheduled order states its site on its windows.
    const seedAdministrationSite = () => {
        if (!editingMedication.value || editingMedication.value.administeringLocation?.id) return;
        if (administrationType.value !== 'prn') return;
        const enrolled = enrolledSiteOf(administrationSites.value);
        if (enrolled) setAdministrationSite(enrolled);
    };

    // The enrollments may still be reading when the dialog opens, so the seed happens on whichever
    // lands second. Guarded on a new order: it must never move a saved one.
    watch([administrationSites, administrationType], () => {
        if (toValue(editDialog) && isNewMedication.value) seedAdministrationSite();
    });

    const prnStart = ref(null);
    // Held here rather than on the order: the PRN segment built on save is where it belongs.
    const prnSelfAdministered = ref(false);
    const prnEnd = ref(null);

    const setOrderPrn = (patch) => {
        const med = editingMedication.value;
        if (!med) return;
        applyOrderPrn(med, patch);
    };

    // What a patient reports in the portal: stated on the carrier, never as a PRN segment or a saved
    // type, so it is information for reconciliation rather than an order the eMAR would act on.
    const reportedAsNeeded = computed(() => administrationType.value !== 'prn' && editingCarrier.value?.asNeeded?.boolean === true);

    const setReportedAsNeeded = (value) => {
        const med = editingMedication.value;
        if (!med) return;
        const carrier = ensureCarrier(med);
        carrier.asNeeded = value ? { boolean: true } : undefined;
    };

    const generatedSig = computed(() => composeSig(editingMedication.value, { administrationType: administrationType.value, currentSegment: currentSegment.value }));

    const doseUnitOptions = Object.keys(DOSE_UNIT_CODES);

    const setDoseQuantity = (patch) => {
        const med = editingMedication.value;
        if (!med) return;
        applyOrderDose(med, patch);
    };

    const setRoute = (value) => {
        const med = editingMedication.value;
        if (!med) return;
        applyOrderRoute(med, value);
    };

    const setAdministrationInstructions = (value) => {
        const med = editingMedication.value;
        if (!med) return;
        applyAdministrationInstructions(med, value);
    };

    const openSegmentDialog = (segment = null, type = null, template = null) => {
        editingSegment.value = segment;
        initialSegmentType.value = type;
        segmentTemplate.value = template;
        segmentDialog.value = true;
    };

    const openTaperDialog = () => {
        taperDialog.value = true;
    };

    const setAdministrationType = (type) => {
        administrationType.value = type;
        // The stored type is the clinician's choice and locks the chooser once saved; a patient
        // reopening their own entry in the portal must not make it for them.
        if (!editingMedication.value || toValue(isPortal)) return;
        editingMedication.value.administrationType = type;
        // Left behind on a switch to a schedule, the order-level site is neither shown nor clearable,
        // and still resolves as the fallback under every window.
        if (type !== 'prn') setAdministrationSite(null);
    };

    // Taper steps carry durations a scheduled or PRN order would keep reading as a taper, so a new
    // medication starts its schedule over.
    const onAdministrationTypeChosen = async (type) => {
        const med = editingMedication.value;
        if (med && type !== administrationType.value && isNewMedication.value && editingSegments.value.length) {
            const confirmed = await confirm({
                header: 'Change administration type',
                message: 'Changing the administration type clears the schedule entered for this medication. Continue?',
                confirm: 'Clear schedule',
                cancel: 'Keep schedule',
            });
            if (!confirmed) return;
            med.dosageInstruction = (med.dosageInstruction || []).filter((entry) => !segmentStartOf(entry));
            delete med.taperNotes;
            editingSegment.value = null;
        }
        setAdministrationType(type);
        if (!med || toValue(isPortal)) return;

        const carrier = carrierOf(med);
        if (type === 'unknown') {
            // Hidden under Unknown, a frequency or limit entered first would still compose into the
            // sig and save as the order.
            if (carrier) {
                carrier.timing = mergeCarrierTiming(carrier, { repeat: undefined });
                carrier.maxPerDay = undefined;
                carrier.minimumInterval = undefined;
            }
            return;
        }
        // One is what almost every order is written for, and the field is required - so it arrives
        // answered rather than as a blank to fill in to get past validation. Not under Unknown, where
        // an untouched 1 would read as a dose somebody stated.
        if (isOrderBeingWritten() && !editingDose.value?.value) setDoseQuantity({ value: 1 });
    };

    // The segment a replacement is seeded from: what is in force today, or the next one written when
    // the whole chain is still ahead. Holds carry no times to inherit.
    const scheduleTemplate = computed(() => {
        const ordered = editingSegments.value.filter((entry) => !isHeld(entry));
        if (!ordered.length) return null;
        const today = todayInServiceZone();
        return [...ordered].reverse().find((entry) => segmentStartOf(entry) <= today) || ordered[0];
    });

    const chooseAdministrationType = (type) => {
        if (type === 'taper') {
            openTaperDialog();
            return;
        }
        // While unsaved there is one definition being written, so re-choosing reopens it.
        if (isNewMedication.value) {
            openSegmentDialog(editingSegments.value[0] ?? null, type);
            return;
        }
        openSegmentDialog(null, type, scheduleTemplate.value);
    };

    const heldSegment = computed(() => (isHeld(currentSegment.value) ? currentSegment.value : null));

    const canHold = computed(() => !isNewMedication.value && editingSegments.value.length > 0 && !heldSegment.value);

    // The resumed schedule's start is what ends the hold: a hold that has started cannot be given an end.
    const releaseHold = () => {
        const template = scheduleTemplate.value;
        openSegmentDialog(null, template && isPrn(template) ? 'prn' : 'scheduled', template);
    };

    const onSegmentSaved = (dosage) => {
        const med = editingMedication.value;
        if (!med) return;

        const entries = [...(med.dosageInstruction || [])];
        // An edited row replaces only itself, new medication or not - replacing the whole chain
        // dropped every other step of a draft taper.
        if (isNewMedication.value && !editingSegment.value) {
            med.dosageInstruction = [...entries.filter((entry) => !segmentStartOf(entry)), dosage];
        } else {
            const index = editingSegment.value ? entries.indexOf(editingSegment.value) : -1;
            if (index >= 0) entries.splice(index, 1, dosage);
            else entries.push(dosage);
            med.dosageInstruction = entries;
        }
        rechainSegments(med);

        editingSegment.value = null;
    };

    // On an unsaved medication a taper replaces whatever was defined; on a saved one the dialog emits
    // only the new steps, which append to a chain that is never rewritten.
    const onTaperSaved = (dosages, { taperNotes } = {}) => {
        const med = editingMedication.value;
        if (!med || !dosages?.length) return;

        const entries = [...(med.dosageInstruction || [])];
        med.dosageInstruction = isNewMedication.value ? [...entries.filter((entry) => !segmentStartOf(entry)), ...dosages] : [...entries, ...dosages];
        rechainSegments(med);

        if (taperNotes) med.taperNotes = taperNotes;
        else delete med.taperNotes;
    };

    const onSegmentDeleted = (segment) => {
        const med = editingMedication.value;
        if (!med || !segment) return;
        med.dosageInstruction = (med.dosageInstruction || []).filter((entry) => entry !== segment);
        rechainSegments(med);
        editingSegment.value = null;
    };

    return {
        segmentDialog,
        editingSegment,
        initialSegmentType,
        segmentTemplate,
        taperDialog,
        editingSegments,
        canEditAdministrationType,
        canEditSchedule,
        canOpenSegment,
        canEditOrderIdentity,
        canEditRoute,
        segmentStartOf,
        describeSegment,
        endSegmentChain,
        reopenSegmentChain,
        discontinueOrder,
        restoreOrder,
        ensureCarrier,
        editingCarrier,
        editingDose,
        editingOrderTiming,
        orderFrequency,
        orderPeriod,
        orderPeriodUnit,
        isOrderBeingWritten,
        requiresDose,
        requiresRecurrence,
        requiresPrnLimits,
        prnOrderIncomplete,
        prnStartsInPast,
        effectiveStart,
        effectiveEnd,
        effectiveEndError,
        prnStartError,
        prnEndError,
        mergeCarrierTiming,
        setOrderTiming,
        prescribedSig,
        editingDoseFreeText,
        currentSegment,
        isSegmentSaved,
        editingHasAdministrations,
        isDoseLocked,
        canEditDose,
        savedScheduleType,
        scheduleSectionCy,
        isTypeLocked,
        administrationType,
        administrationTypeOptions,
        administrationTypeLabel,
        segmentSiteLabel,
        setAdministrationSite,
        seedAdministrationSite,
        prnStart,
        prnSelfAdministered,
        prnEnd,
        setOrderPrn,
        generatedSig,
        reportedAsNeeded,
        setReportedAsNeeded,
        doseUnitOptions,
        setDoseQuantity,
        setRoute,
        setAdministrationInstructions,
        openSegmentDialog,
        openTaperDialog,
        setAdministrationType,
        onAdministrationTypeChosen,
        chooseAdministrationType,
        heldSegment,
        canHold,
        releaseHold,
        onSegmentSaved,
        onTaperSaved,
        onSegmentDeleted,
    };
}
